import { useEffect, useState } from 'react';
import {
  Badge,
  IconButton,
  Tooltip,
  Drawer,
  Box,
  Typography,
  Stack,
  Divider,
  Button,
  CircularProgress,
  ListItemButton,
  ListItemText,
  Alert,
} from '@mui/material';
import {
  NotificationsRounded,
  NotificationsActiveRounded,
  DoneAllRounded,
  SettingsRounded,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTenantStore } from '../../stores/tenant.store';
import { usePermissions } from '../../hooks/use-permissions';
import { notificationApi, type NotificationDto } from '../../lib/api/notification.api';
import { errorText } from '../../lib/error-text';
import { formatDateTime } from '../../lib/i18n-format';

const POLL_MS = 60_000;

export function NotificationsBell() {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  const { currentTenantId } = useTenantStore();
  const { defaultRoute } = usePermissions();
  // Students live in the portal-v2 shell → send them to its (dark, matrix)
  // preferences page; staff/admin keep the standalone settings page so they
  // don't land inside the student chrome.
  const notificationsSettingsPath = defaultRoute.startsWith('/portal-v2')
    ? '/portal-v2/settings/notifications'
    : '/settings/notifications';
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<NotificationDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshCount = async () => {
    if (!currentTenantId) return;
    try {
      const { count } = await notificationApi.countUnread({ tenantId: currentTenantId });
      setUnread(count);
    } catch {
      // silent fail — keep last count
    }
  };

  useEffect(() => {
    refreshCount();
    const id = window.setInterval(refreshCount, POLL_MS);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenantId]);

  const openDrawer = async () => {
    if (!currentTenantId) return;
    setOpen(true);
    setLoading(true);
    setError(null);
    try {
      const { items: fetched } = await notificationApi.list({
        tenantId: currentTenantId,
        limit: 30,
      });
      setItems(fetched);
    } catch (e) {
      setError(errorText(e, 'Error al cargar'));
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async (n: NotificationDto) => {
    if (!currentTenantId) return;
    if (n.status === 'unread') {
      try {
        await notificationApi.markRead({ tenantId: currentTenantId, notificationId: n.id });
        setItems((prev) =>
          prev.map((it) =>
            it.id === n.id ? { ...it, status: 'read', readAt: new Date().toISOString() } : it,
          ),
        );
        setUnread((c) => Math.max(0, c - 1));
      } catch {
        // ignore
      }
    }
    if (n.payload.actionUrl) {
      setOpen(false);
      navigate(n.payload.actionUrl);
    }
  };

  const handleMarkAll = async () => {
    if (!currentTenantId) return;
    try {
      await notificationApi.markAllRead({ tenantId: currentTenantId });
      setItems((prev) =>
        prev.map((it) => ({ ...it, status: 'read', readAt: new Date().toISOString() })),
      );
      setUnread(0);
    } catch {
      // ignore
    }
  };

  return (
    <>
      <Tooltip title={t('bell.title')}>
        <IconButton size="small" onClick={openDrawer}>
          <Badge badgeContent={unread} color="error" max={99}>
            {unread > 0 ? (
              <NotificationsActiveRounded fontSize="small" />
            ) : (
              <NotificationsRounded fontSize="small" />
            )}
          </Badge>
        </IconButton>
      </Tooltip>
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 380 } } }}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
              {t('bell.title')}
            </Typography>
            <Tooltip title={t('bell.preferences')}>
              <IconButton
                size="small"
                onClick={() => {
                  setOpen(false);
                  navigate(notificationsSettingsPath);
                }}
              >
                <SettingsRounded fontSize="small" />
              </IconButton>
            </Tooltip>
            {unread > 0 && (
              <Tooltip title={t('bell.markAllRead')}>
                <IconButton size="small" onClick={handleMarkAll}>
                  <DoneAllRounded fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Box>
        <Divider />
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {loading && (
            <Box textAlign="center" py={4}>
              <CircularProgress size={20} />
            </Box>
          )}
          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          )}
          {!loading && !error && items.length === 0 && (
            <Box textAlign="center" py={6}>
              <NotificationsRounded sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">{t('bell.empty')}</Typography>
            </Box>
          )}
          {items.map((n) => (
            <ListItemButton
              key={n.id}
              onClick={() => handleClick(n)}
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: n.status === 'unread' ? 'action.hover' : 'transparent',
                alignItems: 'flex-start',
                py: 1.25,
              }}
            >
              <ListItemText
                primary={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {n.status === 'unread' && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Typography variant="body2" fontWeight={n.status === 'unread' ? 700 : 500}>
                      {n.payload.title}
                    </Typography>
                  </Stack>
                }
                secondary={
                  <>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="span"
                      display="block"
                    >
                      {n.payload.body}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" component="span">
                      {formatDateTime(n.createdAt)}
                    </Typography>
                  </>
                }
              />
            </ListItemButton>
          ))}
        </Box>
        <Divider />
        <Box sx={{ p: 1.5, textAlign: 'center' }}>
          <Button size="small" onClick={() => setOpen(false)}>
            {t('bell.close')}
          </Button>
        </Box>
      </Drawer>
    </>
  );
}
